import { Plate } from '../components/Card';
import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { getPfAccounts, getPfPnl, getPfOperations, getPfCompanies } from '../api';

const fmt = (n, short = false) => {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n);
  if (short && abs >= 1000000) return (n / 1000000).toFixed(1) + ' M';
  if (short && abs >= 1000) return (n / 1000).toFixed(0) + ' K';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';
};

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${months[parseInt(mo) - 1]} ${y.slice(2)}`;
};

const BANK_COLORS = {
  sber: '#0e9f6e',
  tinkoff: '#00a7dc',
  tochka: '#6B4EFF',
  vtb: '#009FDF',
  otp: '#E4003A',
  other: '#00a7dc',
};

const BANK_NAMES = {
  sber: 'Сбербанк',
  tinkoff: 'Т-Банк',
  tochka: 'Точка',
  vtb: 'ВТБ',
  otp: 'ОТП Банк',
  other: 'Банк',
};

const BankIcon = ({ brand, size = 22 }) => {
  const color = BANK_COLORS[brand] || '#00a7dc';
  const text = { sber: 'С', tinkoff: 'T', tochka: '•', vtb: 'В', otp: 'O', other: '₽' }[brand] || '₽';
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 4,
      background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.55, fontWeight: 800,
      color: brand === 'tinkoff' ? '#111' : '#fff', flexShrink: 0,
    }}>
      {text}
    </div>
  );
};

const AccountCard = ({ acc }) => {
  const color = BANK_COLORS[acc.bank_brand] || '#00a7dc';
  const isPositive = acc.balance >= 0;
  return (
    <div style={{
      background: 'var(--bg2)', border: `1px solid var(--border)`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 10, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 6,
      minWidth: 180, flex: '1 1 180px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <BankIcon brand={acc.bank_brand} size={20} />
        <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.2 }}>
          {acc.title}
          {acc.acc_num && <span style={{ color: 'var(--text2)' }}> ···{acc.acc_num}</span>}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, color: isPositive ? color : '#e04848' }}>
        {fmt(acc.balance)}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text2)' }}>
        {acc.company || ''}
        {acc.account_type === 'Cash' && ' 💵 Наличные'}
      </div>
    </div>
  );
};

const CUSTOM_TOOLTIP = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg3)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '10px 14px', fontSize: 12,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text)' }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color, marginBottom: 2 }}>
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

export default function PlanFactIO() {
  const [tab, setTab] = useState('overview');
  const [accounts, setAccounts] = useState([]);
  const [pnl, setPnl] = useState([]);
  const [operations, setOperations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [months, setMonths] = useState(6);
  const [opFilter, setOpFilter] = useState({ dateFrom: '', dateTo: '', companyId: '', search: '' });

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (tab === 'pnl') loadPnl();
  }, [months]);

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      const [accs, pnlData, comps] = await Promise.all([
        getPfAccounts(),
        getPfPnl(months),
        getPfCompanies(),
      ]);
      setAccounts(accs);
      setPnl(pnlData);
      setCompanies(comps);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Ошибка загрузки PlanFact');
    }
    setLoading(false);
  }

  async function loadPnl() {
    try {
      const data = await getPfPnl(months);
      setPnl(data);
    } catch {}
  }

  async function loadOperations() {
    try {
      const params = {};
      if (opFilter.dateFrom) params.date_from = opFilter.dateFrom;
      if (opFilter.dateTo) params.date_to = opFilter.dateTo;
      if (opFilter.companyId) params.company_id = +opFilter.companyId;
      const data = await getPfOperations({ ...params, limit: 300 });
      setOperations(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }

  // ─── Aggregates ──────────────────────────────────────────────────────────

  const totalBalance = accounts.reduce((s, a) => s + (a.balance || 0), 0);
  const bankBalance = accounts.filter(a => a.account_type === 'Card').reduce((s, a) => s + a.balance, 0);
  const cashBalance = accounts.filter(a => a.account_type === 'Cash').reduce((s, a) => s + a.balance, 0);

  // Last month P&L
  const lastPnl = pnl[pnl.length - 1] || {};
  const prevPnl = pnl[pnl.length - 2] || {};

  // Group accounts by company
  const byCompany = accounts.reduce((acc, a) => {
    const key = a.company || '—';
    if (!acc[key]) acc[key] = { company: key, items: [], total: 0 };
    acc[key].items.push(a);
    acc[key].total += a.balance;
    return acc;
  }, {});

  // Chart data
  const chartData = pnl.map(m => ({
    period: monthLabel(m.period),
    'Доходы': m.income,
    'Расходы': m.expense,
    'Прибыль': m.profit,
  }));

  const TABS = [
    { id: 'overview', label: '📊 Обзор' },
    { id: 'accounts', label: '🏦 Счета' },
    { id: 'pnl', label: '📈 П/Ф' },
    { id: 'operations', label: '💳 Операции' },
  ];

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>⏳</div>
      Загружаем данные из ПланФакта...
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 10 }}>❌</div>
      <div style={{ color: '#e04848' }}>{error}</div>
      <button onClick={loadAll} style={{ marginTop: 16, padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
        Повторить
      </button>
    </div>
  );

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div className="kp-hero">
        <Plate>Финансы · ПланФакт</Plate>
        <h1>ПланФакт — управленческий учёт</h1>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8 }}>
          Данные из planfact.io · {accounts.length} счетов · {companies.length} компаний
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Всего в банках и кассе', value: fmt(totalBalance), color: '#00a7dc', icon: '💰' },
          { label: 'Безналичные счета', value: fmt(bankBalance), color: '#0e9f6e', icon: '🏦' },
          { label: 'Наличные кассы', value: fmt(cashBalance), color: '#e08a00', icon: '💵' },
          { label: `Доходы ${monthLabel(lastPnl.period)}`, value: fmt(lastPnl.income), color: '#0e9f6e', icon: '📈' },
          { label: `Расходы ${monthLabel(lastPnl.period)}`, value: fmt(lastPnl.expense), color: '#e04848', icon: '📉' },
          { label: `Прибыль ${monthLabel(lastPnl.period)}`, value: fmt(lastPnl.profit), color: lastPnl.profit >= 0 ? '#0e9f6e' : '#e04848', icon: '⚡' },
        ].map(({ label, value, color, icon }) => (
          <div key={label} style={{
            background: 'var(--bg3)',
            borderRadius: 14, padding: '14px 16px 16px', minWidth: 160, flex: '1 1 160px',
          }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>{icon} {label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'operations' && !operations.length) loadOperations(); }}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: '8px 8px 0 0',
              background: tab === t.id ? 'var(--accent)' : 'transparent',
              color: tab === t.id ? '#fff' : 'var(--text2)',
              fontWeight: tab === t.id ? 700 : 400, cursor: 'pointer', fontSize: 13,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── Overview ─────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div>
          {/* Chart */}
          {chartData.length > 0 && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
              <h3 style={{ marginBottom: 14, fontSize: 14 }}>Доходы / Расходы / Прибыль за {months} месяцев</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ left: 0, right: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: 'var(--text2)' }} />
                  <YAxis tickFormatter={v => fmt(v, true)} tick={{ fontSize: 10, fill: 'var(--text2)' }} width={60} />
                  <Tooltip content={<CUSTOM_TOOLTIP />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Доходы" fill="#0e9f6e" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Расходы" fill="#e04848" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Прибыль" fill="#00a7dc" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Accounts by company */}
          <h3 style={{ marginBottom: 12, fontSize: 14 }}>Счета по компаниям</h3>
          {Object.values(byCompany)
            .sort((a, b) => b.total - a.total)
            .map(group => (
              <div key={group.company} style={{
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{group.company}</div>
                  <div style={{
                    fontWeight: 800, fontSize: 16,
                    color: group.total >= 0 ? '#0e9f6e' : '#e04848',
                  }}>
                    {fmt(group.total)}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {group.items.map(acc => (
                    <div key={acc.id} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: 'var(--bg3)', borderRadius: 8, padding: '5px 10px',
                      fontSize: 12,
                    }}>
                      <BankIcon brand={acc.bank_brand} size={16} />
                      <span style={{ color: 'var(--text2)' }}>{acc.title}</span>
                      <span style={{
                        fontWeight: 700,
                        color: acc.balance > 0 ? '#00a7dc' : acc.balance < 0 ? '#e04848' : 'var(--text2)',
                      }}>
                        {fmt(acc.balance)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* ─── Accounts ─────────────────────────────────────────────────────── */}
      {tab === 'accounts' && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            {['Card', 'Cash'].map(type => {
              const filtered = accounts.filter(a => a.account_type === type);
              const total = filtered.reduce((s, a) => s + a.balance, 0);
              return (
                <div key={type} style={{
                  background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 10, padding: '10px 16px', minWidth: 180,
                }}>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                    {type === 'Card' ? '🏦 Безналичные' : '💵 Наличные'}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#00a7dc' }}>{fmt(total)}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>{filtered.length} счетов</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {accounts.map(acc => <AccountCard key={acc.id} acc={acc} />)}
          </div>
        </div>
      )}

      {/* ─── P&L ──────────────────────────────────────────────────────────── */}
      {tab === 'pnl' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>Показать за</span>
            {[3, 6, 12].map(m => (
              <button key={m} onClick={() => setMonths(m)} style={{
                padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border)',
                background: months === m ? 'var(--accent)' : 'var(--bg3)',
                color: months === m ? '#fff' : 'var(--text2)',
                cursor: 'pointer', fontSize: 12,
              }}>
                {m} мес.
              </button>
            ))}
          </div>

          {/* Chart */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: 'var(--text2)' }} />
                <YAxis tickFormatter={v => fmt(v, true)} tick={{ fontSize: 10, fill: 'var(--text2)' }} width={62} />
                <Tooltip content={<CUSTOM_TOOLTIP />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Доходы" stroke="#0e9f6e" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Расходы" stroke="#e04848" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Прибыль" stroke="#00a7dc" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div style={{ overflow: 'auto', borderRadius: 10, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg3)' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: 'var(--text2)', borderBottom: '1px solid var(--border)' }}>Период</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#0e9f6e', borderBottom: '1px solid var(--border)' }}>Доходы</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#e04848', borderBottom: '1px solid var(--border)' }}>Расходы</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: '#00a7dc', borderBottom: '1px solid var(--border)' }}>Прибыль</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: 'var(--text2)', borderBottom: '1px solid var(--border)' }}>Маржа</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 11, color: 'var(--text2)', borderBottom: '1px solid var(--border)' }}>Операций</th>
                </tr>
              </thead>
              <tbody>
                {[...pnl].reverse().map(m => {
                  const margin = m.income > 0 ? ((m.profit / m.income) * 100).toFixed(1) : '—';
                  return (
                    <tr key={m.period} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{monthLabel(m.period)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: '#0e9f6e', fontWeight: 600 }}>{fmt(m.income)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: '#e04848', fontWeight: 600 }}>{fmt(m.expense)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: m.profit >= 0 ? '#00a7dc' : '#e04848' }}>
                        {fmt(m.profit)}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text2)' }}>
                        {margin !== '—' ? `${margin}%` : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--text2)' }}>{m.ops_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Operations ───────────────────────────────────────────────────── */}
      {tab === 'operations' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <input type="date" value={opFilter.dateFrom}
              onChange={e => setOpFilter(f => ({ ...f, dateFrom: e.target.value }))}
              style={{ padding: '6px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 12 }} />
            <input type="date" value={opFilter.dateTo}
              onChange={e => setOpFilter(f => ({ ...f, dateTo: e.target.value }))}
              style={{ padding: '6px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 12 }} />
            <select value={opFilter.companyId}
              onChange={e => setOpFilter(f => ({ ...f, companyId: e.target.value }))}
              style={{ padding: '6px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 12 }}>
              <option value="">Все компании</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <button onClick={loadOperations}
              style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Загрузить
            </button>
          </div>

          {operations.length === 0 ? (
            <div style={{ color: 'var(--text2)', padding: 30, textAlign: 'center' }}>
              Установите фильтры и нажмите «Загрузить»
            </div>
          ) : (
            <div style={{ overflow: 'auto', borderRadius: 10, border: '1px solid var(--border)' }}>
              {/* Summary */}
              <div style={{ padding: '10px 16px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 20, fontSize: 13 }}>
                <span>Операций: <strong>{operations.length}</strong></span>
                <span style={{ color: '#0e9f6e' }}>Доходы: <strong>{fmt(operations.filter(o => o.type === 'Income').reduce((s, o) => s + o.value, 0))}</strong></span>
                <span style={{ color: '#e04848' }}>Расходы: <strong>{fmt(operations.filter(o => o.type !== 'Income').reduce((s, o) => s + o.value, 0))}</strong></span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg3)' }}>
                    {['Дата', 'Тип', 'Сумма', 'Категория', 'Счёт', 'Компания', 'Комментарий'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Сумма' ? 'right' : 'left', fontSize: 11, color: 'var(--text2)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {operations.slice(0, 200).map((op, i) => (
                    <tr key={op.id || i} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '7px 12px', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--text2)' }}>{op.date}</td>
                      <td style={{ padding: '7px 12px' }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                          background: op.type === 'Income' ? 'rgba(14,159,110,0.12)' : 'rgba(224,72,72,0.12)',
                          color: op.type === 'Income' ? '#0e9f6e' : '#e04848',
                        }}>
                          {op.type === 'Income' ? '+ Доход' : '− Расход'}
                        </span>
                      </td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: op.type === 'Income' ? '#0e9f6e' : '#e04848' }}>
                        {op.type === 'Income' ? '+' : '−'}{Math.abs(op.value).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                      </td>
                      <td style={{ padding: '7px 12px', fontSize: 11, color: 'var(--text2)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.category || '—'}</td>
                      <td style={{ padding: '7px 12px', fontSize: 11, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{op.account || '—'}</td>
                      <td style={{ padding: '7px 12px', fontSize: 11, color: 'var(--text2)', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.company || '—'}</td>
                      <td style={{ padding: '7px 12px', fontSize: 11, color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.comment || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
